import {
	autoinject,
	bindable,
	computedFrom,
	TaskQueue,
} from 'aurelia-framework';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';

import { BatchProcessingCentreApi } from '~/api/batch-processing-centre-api';
import { AppRouteID } from '~/app-routes';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import { CustomisationConfig } from '~/components/customisation/customisation-config';
import {
	SearchFieldModel,
	SearchType,
} from '~/components/field/common/search-definition';
import { SelectOption } from '~/components/field/common/select';
import { FilterType } from '~/components/filter/filter-options';
import { FormType } from '~/components/form/form';
import {
	getErrorOrWarningStatus,
	getNoteIconStatus,
	IconStatusError,
	IconStatusItem,
	IconStatusList,
} from '~/components/icon-status-list/icon-status-list';
import { Notification } from '~/components/notification/notification';
import { ProgressBar } from '~/components/progress-bar/progress-bar';
import { CommonURLParam } from '~/constants';
import { BatchProcessingConfig } from '~/forms/batch-processing-centre/batch-processing-config';
import {
	ProcessBatchDialog,
	ProcessBatchDialogModel,
} from '~/forms/batch-processing-centre/process-batch-dialog';
import { RestoreBatchDialog } from '~/forms/batch-processing-centre/restore-batch-dialog';
import ClassicForm from '~/forms/classic-form';
import { ApiCorrelationId } from '~/models/entities/api-correlation-id';
import {
	BatchProcess,
	BatchProcessFieldName,
	BatchProgressStatus,
} from '~/models/entities/batch-process';
import { createFilterModel } from '~/models/filter-model';
import { FormModel } from '~/models/form-model';
import { Modifiable } from '~/models/form-model';
import { DialogUtils } from '~/utils/dialog-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { generateLinkNoCustomisationParam } from '~/utils/router-utils';

import { ClassicPanel } from '~/components/classic-panel/classic-panel';
import { ClassicPanelDialogModel } from '~/components/classic-panel/classic-panel-dialog';
import { ClassicPanelDialogUtils } from '~/components/classic-panel/classic-panel-dialog-utils';
import { CustomisationEditorSelectDialogManager } from '~/forms/customisation-editor/customisation-editor-select-dialog-manager';
import { HiddenTarget } from '~/models/column-definition';
import { DataTypeEnum } from '~/models/data-type';
import { FormParameters, FormParameterType } from '~/models/form-parameters';
import { BLANK_MEMBER } from '~/models/membership';
import { NewBatchDialog } from './new-batch-dialog';

enum BatchProgressParentChildIndicator {
	CHILD = 'C',
	PARENT = 'P',
}

@autoinject
export class BatchProcessingCentre extends ClassicForm<BatchProcessFieldName> {
	/**
	 * progressBar is a React component that is not attached to the main DOM
	 * and we just get the HTML for the DataTable each render.
	 *
	 * @deprecated This is a dirty hack, do not copy
	 */
	private readonly progressBar = new ProgressBar(undefined);

	/**
	 * List of options for "Batch Code Group" <a-select> field
	 */
	private batchGroupList: SelectOption[] = [];

	/**
	 * Context menu IDs
	 */
	private readonly contextMenuIds = {
		processBatchMenu: 'processBatchMenuId',
		restoreBatchMenu: 'restoreBatchMenuId',
		batchMessagesMenu: 'batchMessagesMenuId',
		transactionCentreMenu: 'transactionCentreMenuId',
		reportingCentreMenu: 'reportingCentreMenuId',
		viewSplitGroupMenu: 'viewSplitGroupMenuId',
	};

	/**
	 * Table filter flag for "Show Cancelled Batches"
	 */
	@bindable private isShowCancelledBatches: boolean = false;

	/**
	 * Table filter flag for "Show My Batches Only"
	 */
	@bindable private isShowMyBatchesOnly: boolean = false;

	private static readonly FUNCTION_NAME: string = 'batches';
	private slideoutProgressBar: ProgressBar;

	private readonly createFormModel: FormModel;

	constructor(
		readonly customisationEditorSelectDialogManager: CustomisationEditorSelectDialogManager,
		public readonly dialogUtils: DialogUtils,
		public readonly classicPanelDialogUtils: ClassicPanelDialogUtils,
		private readonly taskQueue: TaskQueue,
		private readonly validationController: ValidationController,
		...rest
	) {
		super(
			BatchProcessingCentre.FUNCTION_NAME, // functionName
			true, // canSave
			false, // canDelete
			true, // hasNotes
			false, // hasAuditDetails
			false, // canAdd
			4, // visibleColumns
			[
				// all possible form parameters
				BatchProcessFieldName.BSz_Batch_Code,
				BatchProcessFieldName.BSz_Fund,
				BatchProcessFieldName.BSz_Member,
			],
			...rest,
		);
		this.contextMenuMaxItemsCount = 8;
		this.functionName = BatchProcessingCentre.FUNCTION_NAME;
		this.tableConfig.tableColumns = [
			{
				name: BatchProcessFieldName.BSl_Batch_Reference,
				label: 'Batch Reference',
				dataType: { name: DataTypeEnum.Long },
			},
			{
				name: BatchProcessFieldName.ZYl_Batch_Error_Level,
				label: '',
				dataType: { name: DataTypeEnum.Long },
				render: (fieldValue, columnInfo, record) =>
					this.renderBatchFlags(fieldValue, columnInfo, record),
				unsortable: true,
				filter: createFilterModel({ hidden: true }),
			},
			{
				name: BatchProcessFieldName.BSd_Batch_Created,
				label: 'Created Date',
				dataType: { name: DataTypeEnum.Date },
			},
			{
				name: BatchProcessFieldName.BSz_Fund,
				label: 'Fund',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: BatchProcessFieldName.BSz_Member,
				label: 'Member',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: BatchProcessFieldName.BSz_Batch_Code,
				label: 'Batch Code',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: BatchProcessFieldName.BSz_Description,
				label: 'Description',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: BatchProcessFieldName.BSz_Batch_Group,
				label: 'Code Group',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: BatchProcessFieldName.BSc_Status,
				label: 'Status',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: BatchProcessFieldName.ZYz_Batch_Progress_Status,
				label: '',
				dataType: { name: DataTypeEnum.String },
				render: (fieldValue, columnInfo, row) =>
					this.renderStatus(fieldValue, columnInfo, row),
				unsortable: true,
				filter: createFilterModel({ hidden: true }),
			},
			// Filter Only Fields
			{
				name: BatchProcessFieldName.BSl_Master_Batch_Ref,
				label: 'Master Batch Reference',
				hiddenFrom: [HiddenTarget.TABLE],
				dataType: { name: DataTypeEnum.Long },
			},
			{
				// NOTE: BatchProcessFieldName.BSl_Job_Ref is required to get
				// the value of BatchProcessFieldName.ZYv_Correlation_ID
				// Please do not drop it
				name: BatchProcessFieldName.BSl_Job_Ref,
				label: 'Background Job Reference',
				hiddenFrom: [
					HiddenTarget.TABLE,
					HiddenTarget.FILTER,
					HiddenTarget.EXPORT,
				],
				dataType: { name: DataTypeEnum.Long },
			},
			{
				name: BatchProcessFieldName.ZYv_Correlation_ID,
				label: 'Correlation ID',
				dataType: { name: DataTypeEnum.String },
				unsortable: true,
				enableToolTip: true,
			},
		];
		this.tableConfig.rowIdKey = BatchProcessFieldName.BSi_Identity;
		this.tableConfig.multiColumnOrder = [
			{
				name: BatchProcessFieldName.BSl_Batch_Reference,
				direction: 'desc',
			},
		];

		// Setup create form
		{
			const fieldModel: SearchFieldModel = FormModelUtils.fieldModelFrom({
				fieldValidationList: [],
				modifiable: Modifiable.TRUE,
				name: 'Batch_Code',
				value: '',
				label: '',
				visible: true,
			});
			fieldModel.searchType = SearchType.BATCH_CODES;
			fieldModel.additionalFilters = [
				// NOTE(Jae): 2020-03-05 - WEBUI-3118
				// Filter out deleted records, as defined in the JIRA comments.
				{
					name: 'BTc_Deleted',
					value: 'Y',
					andJoin: true,
					operation: FilterType.NOT_EQUALS,
				},
				// NOTE(Jae): 2020-03-05 - WEBUI-3118
				// Filter by "" and "S" as defined in the JIRA.
				{
					name: 'BTc_Quote_Batch',
					value: ',S',
					andJoin: true,
					operation: FilterType.IN,
				},
			];
			fieldModel.filterBy = [
				{
					col: 'Batch_Group',
					targetCol: 'BTz_Batch_Group',
					ignoreBlank: true,
				},
			];
			fieldModel.displayResultsOnFocus = true;
			this.createFormModel = {
				fields: {
					Batch_Group: FormModelUtils.fieldModelFrom({
						name: 'Batch_Group',
						label: '',
						value: '',
						fieldValidationList: [],
						modifiable: Modifiable.TRUE,
						visible: true,
					}),
					Batch_Code: fieldModel,
				},
			};
		}
	}

	private computeProgressBarLabelForRow(row: BatchProcess): string {
		return this.computeProgressBarLabel(
			row.ZYl_Batch_Progress_Total,
			row.ZYl_Batch_Progress_Current,
			row.ZYz_Batch_Progress_Status,
		);
	}

	private computeProgressBarLabel(
		totalValue: number,
		currentValue: number,
		batchProgressStatus: BatchProgressStatus,
	): string {
		const stepRangeText = BatchProcessingCentre.computeStepRangeText(
			totalValue,
			currentValue,
			batchProgressStatus,
		);
		if (stepRangeText !== '') {
			return this.i18n.tr('batch-processing-centre:step-completed', {
				number: stepRangeText,
			});
		} else {
			switch (batchProgressStatus) {
				case BatchProgressStatus.COMPLETED:
					return this.i18n.tr(
						'batch-processing-centre:all-steps-completed',
					);
				case BatchProgressStatus.CANCELLED:
					return this.i18n.tr(
						'batch-processing-centre:batch-cancelled',
					);
				default:
					return '';
			}
		}
	}

	protected activate(
		params: { [param: string]: any },
		routeConfig: RouteConfig,
		navigationInstruction: NavigationInstruction,
	): Promise<any> {
		return super
			.activate(params, routeConfig, navigationInstruction)
			.then(() => {
				const config = this.customisationConfig as CustomisationConfig;
				if (config && config.functionConfig) {
					const batchCode = (
						config.functionConfig as BatchProcessingConfig
					).singleBatchCode;
					if (batchCode && batchCode.length === 6) {
						const fields = this.createFormModel.fields;
						fields.Batch_Code.modifiable = Modifiable.FALSE;
						fields.Batch_Code.value = batchCode;
						fields.Batch_Group.modifiable = Modifiable.FALSE;

						const batchCodeParam: FormParameters = {
							paramType: CommonURLParam.BATCHES,
							value: batchCode,
							name: BatchProcessFieldName.BSz_Batch_Code,
						};
						this.selectedParameters.push(batchCodeParam);
					} else {
						this.processBatchCodes();
					}
				}
			})
			.catch(FetchUtils.logAndDisplayError);
	}

	/**
	 * Filter table records according to batch code if `singleBatchCode` specified in customisation
	 * @override
	 */
	protected getFilteredParameterTypes(): FormParameterType[] {
		return [
			...super.getFilteredParameterTypes(),
			CommonURLParam.BATCHES,
			CommonURLParam.API_CORRELATION_ID,
		];
	}

	/**
	 * Registered the value of {@link CommonURLParam.API_CORRELATION_ID} to the filter
	 *
	 * @param params request parameters
	 */
	protected setSelectedParameters(params: {
		[paramName: string]: any;
	}): void {
		super.setSelectedParameters(params);
		const correlationId = params[CommonURLParam.API_CORRELATION_ID];
		if (correlationId) {
			this.selectedParameters.push({
				paramType: CommonURLParam.API_CORRELATION_ID,
				value: correlationId,
				name: BatchProcessFieldName.ZYv_Correlation_ID,
			});
		}
	}

	protected bind() {
		// NOTE(Jae): 2020-03-05
		// Set labels *after* I18N data has loaded in.
		this.createFormModel.fields.Batch_Group.label = this.i18n.tr(
			'batch-processing-centre:batch-code-group',
		);
		this.createFormModel.fields.Batch_Code.label = this.i18n.tr(
			'batch-processing-centre:batch-code',
		);
	}

	/**
	 * Render batch controls: notes; warnings/error; parent/child indicator
	 */
	private renderBatchFlags(_, __, row: BatchProcess): string {
		const result: IconStatusItem[] = [];

		// Note
		result.push(getNoteIconStatus(row.BSc_Notes === 'Y'));

		// Error / Warning
		const iconStatus: IconStatusError = row.ZYl_Batch_Error_Level;
		if (iconStatus !== null && iconStatus !== undefined) {
			result.push(getErrorOrWarningStatus(iconStatus));
		}

		// Master/Child indicator
		switch (row.ZYc_Master_Child_Indicator) {
			case BatchProgressParentChildIndicator.CHILD:
				result.push({
					title: this.i18n.tr('acurity-table.child'),
					class: 'fa fa-c',
				});
				break;
			case BatchProgressParentChildIndicator.PARENT:
				result.push({
					title: this.i18n.tr('acurity-table.parent'),
					class: 'fa fa-p',
				});
				break;
		}
		return IconStatusList(result);
	}

	/**
	 * Render batch status
	 */
	private renderStatus(_, __, row: BatchProcess) {
		// NOTE: Jake: 2019-10-02 - WEBUI-2494
		// This is a really nasty hack for rendering a React component inside
		// a DataTable. Please nobody copy this pattern.
		const progressBar = this.getProgressBarFromRowData(
			row,
			this.progressBar,
		);

		// if progress bar is empty, it does not matter what fill colour is
		// set fill colour to light so label colour becomes dark and can be seen
		if (progressBar.nowValue === 0) {
			progressBar.color = ProgressBar.ThemeColor.Light;
		}

		// Render and return outerHTML
		progressBar.render();
		return progressBar.getHTML();
	}

	private static computeStepRangeText(
		totalValue: number,
		currentValue: number,
		batchProgressStatus: BatchProgressStatus,
	): string {
		if (
			!(
				batchProgressStatus === BatchProgressStatus.COMPLETED ||
				batchProgressStatus === BatchProgressStatus.CANCELLED
			)
		) {
			// Normal slash will be escape character as prevent XSS, using Unicode Character 'DIVISION SLASH ∕' \u2215 instead
			return `${currentValue} \u2215 ${totalValue}`;
		}
		return '';
	}

	private getProgressBarFromRowData(
		row: BatchProcess,
		progressBar: ProgressBar,
	): ProgressBar {
		return this.getProgressBar(
			row.ZYl_Batch_Progress_Total,
			row.ZYl_Batch_Progress_Current,
			row.ZYz_Batch_Progress_Status,
			progressBar,
		);
	}

	private getProgressBar(
		totalValue: number,
		currentValue: number,
		batchProgressStatus: BatchProgressStatus,
		progressBar: ProgressBar,
	): ProgressBar {
		progressBar.maxValue = totalValue;
		progressBar.nowValue = currentValue;
		progressBar.label = BatchProcessingCentre.computeStepRangeText(
			totalValue,
			currentValue,
			batchProgressStatus,
		);
		progressBar.animated = false;
		progressBar.striped = false;

		// DEBUG: Force a specific status to see how it looks without needing a real record
		// (need this silly if-statement otherwise TS compiler complains)
		// if (progressBar.label || !progressBar.label) {
		// 	batchProgressStatus = BatchProgressStatus.AWAITS_AUTHORISATION;
		// }

		let textStatus = '';
		switch (batchProgressStatus) {
			case BatchProgressStatus.AWAITS_AUTHORISATION:
				progressBar.color = ProgressBar.ThemeColor.Warning;
				progressBar.animated = true;
				progressBar.striped = true;
				textStatus = this.i18n.tr(
					'batch-processing-centre:awaits-authorisation',
				);
				break;

			case BatchProgressStatus.CANCELLED:
				progressBar.color = ProgressBar.ThemeColor.Light;
				progressBar.nowValue = progressBar.maxValue;
				break;

			case BatchProgressStatus.COMPLETED:
				progressBar.color = ProgressBar.ThemeColor.Success;
				progressBar.nowValue = progressBar.maxValue;
				break;

			case BatchProgressStatus.ERROR:
				progressBar.color = ProgressBar.ThemeColor.Danger;
				textStatus = this.i18n.tr('notifications.error');
				break;

			case BatchProgressStatus.IN_PROGRESS_WITH_ERROR:
				progressBar.color = ProgressBar.ThemeColor.Danger;
				progressBar.animated = true;
				progressBar.striped = true;
				textStatus = this.i18n.tr('notifications.error');
				break;

			case BatchProgressStatus.IN_PROGRESS:
				progressBar.color = ProgressBar.ThemeColor.Success;
				progressBar.animated = true;
				progressBar.striped = true;
				break;

			default:
				const exhaustiveCheck: never = batchProgressStatus;
		}
		if (textStatus !== '') {
			if (progressBar.label) {
				progressBar.label += ' - ' + textStatus;
			} else {
				progressBar.label = textStatus;
			}
		}

		return progressBar;
	}

	/**
	 * Context menu config
	 * @override
	 */
	@computedFrom('formModel', 'contextMenuIds', 'contextMenuItemsClassicForm')
	public get contextMenuItems(): ContextMenuItem[] {
		if (this.contextMenuItemsClassicForm.length === 0) {
			// Do not render anything if contextMenuItemsClassicForm is empty.
			// This means it is still loading.
			return this.contextMenuItemsClassicForm;
		}
		const contextMenuItems: ContextMenuItem[] = [
			...this.contextMenuItemsClassicForm,
			{
				id: this.contextMenuIds.processBatchMenu,
				label: this.i18n.tr('batch-processing-centre:process-batch'),
				iconClass: 'fa-play',
				onClick: () => this.showProcessBatchDialog(),
				disabled:
					!this.isProcessBatchButtonVisible || this.isBatchLocked,
			},
			{
				id: this.contextMenuIds.restoreBatchMenu,
				label: this.i18n.tr('restore-batch'),
				iconClass: 'fa-undo',
				onClick: () => this.showRestoreBatchDialog(),
				disabled:
					!this.isRestoreBatchButtonVisible || this.isBatchLocked,
			},
			...this.getCommonContextMenuItems(),
			{
				id: this.contextMenuIds.viewSplitGroupMenu,
				label: this.i18n.tr('batch-processing-centre:view-split-group'),
				iconClass: 'fa-eye',
				onClick: () => this.viewSplitGroupFilter(),
			},
		];

		ApiCorrelationId.registerApiAuditLinkIn(
			contextMenuItems,
			this.i18n,
			this.formModel?.fields?.ZYv_Correlation_ID?.value,
		);

		return contextMenuItems;
	}

	/**
	 * @override
	 */
	@computedFrom('formModel', 'contextMenuIds')
	public get slideoutContextMenu(): ContextMenuItem[] {
		if (!this.formModel) {
			return [];
		}

		const formModelFields = this.formModel?.fields;
		const batchParams = {};
		if (formModelFields) {
			batchParams[CommonURLParam.BATCH_REF] =
				formModelFields.BSl_Batch_Reference.value;
			batchParams[CommonURLParam.BATCH_ID] =
				formModelFields.BSi_Identity.value;
			batchParams[CommonURLParam.FUND] = formModelFields.BSz_Fund.value;
			if (
				formModelFields.BSz_Member.value &&
				formModelFields.BSz_Member.value !== BLANK_MEMBER
			) {
				batchParams[CommonURLParam.MEMBER] =
					formModelFields.BSz_Member.value;
			}
		}
		const contextMenuItems: ContextMenuItem[] = [
			...this.getCommonContextMenuItems(),
			{
				id: this.contextMenuIds.transactionCentreMenu,
				label: this.i18n.tr('route-title.transaction-centre'),
				iconClass: 'fas fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.TransactionCentre,
					batchParams,
				),
			},
			{
				id: this.contextMenuIds.reportingCentreMenu,
				label: this.i18n.tr('route-title.reporting-centre'),
				iconClass: 'fas fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.ReportingCentre,
				),
			},
		];

		ApiCorrelationId.registerApiAuditLinkIn(
			contextMenuItems,
			this.i18n,
			this.formModel?.fields?.ZYv_Correlation_ID?.value,
		);

		return contextMenuItems;
	}

	private getCommonContextMenuItems(): ContextMenuItem[] {
		const formModelFields = this.formModel?.fields;
		const batchParams = {};
		if (formModelFields) {
			batchParams[CommonURLParam.BATCH_REF] =
				formModelFields.BSl_Batch_Reference.value;
			batchParams[CommonURLParam.BATCH_ENV] =
				formModelFields.BSc_Exit.value;
		}

		return [
			{
				id: this.contextMenuIds.batchMessagesMenu,
				label: this.i18n.tr('batch-processing-centre:batch-messages'),
				iconClass: 'fas fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.TransactionMessages,
					batchParams,
				),
			},
		];
	}

	/**
	 * 'View Split Group' context menu action
	 */
	private readonly viewSplitGroupFilter = () => {
		const masterRef = this.getFormModelFieldValue(
			BatchProcessFieldName.BSl_Master_Batch_Ref,
		);

		if (masterRef === undefined || masterRef === '0') {
			Notification.warning(
				this.i18n.tr(
					'batch-processing-centre:selected-record-does-not-have-master',
				),
			);
			return;
		}

		const columnInfo = this.recordSelectorTable.tableColumnInfo.find(
			(col) => col.name === BatchProcessFieldName.BSl_Master_Batch_Ref,
		);
		this.recordSelectorTable.clearFilterOptions();
		columnInfo.filter.value = masterRef;
		this.refreshAjaxTableData();
	};

	/**
	 * Fetch batch codes for the "Create a New Batch" card
	 */
	private async processBatchCodes() {
		const fieldModel = this.createFormModel.fields.Batch_Group;
		if (!fieldModel) {
			throw new Error(
				'Unexpected error. Missing Batch_Group field from createFormModel',
			);
		}

		// Get code groups to be displayed on <a-select>
		const codeList: SelectOption[] = [];
		{
			const hasGroup: { [group: string]: boolean } = {};
			// NOTE(Jae): 2020-03-06 - WEBUI-3118
			// This code originally implemented by someone else worked this way.
			// Currently it returns about 950 batch codes and just finds the unique
			// group names from it. There's room to make this faster in the future by
			// only querying for groups.
			const batchCodes = await BatchProcessingCentreApi.getBatchCodes();
			for (const batchCode of batchCodes) {
				const group = batchCode.group.trim();
				if (hasGroup[group] !== undefined) {
					// Skip if already added
					continue;
				}
				codeList.push({
					displayName: group === '' ? 'All' : group,
					value: group,
				});
				hasGroup[group] = true;
			}
		}

		// Set first item in list to be default
		if (codeList && codeList.length > 0) {
			this.batchGroupList = codeList;
			fieldModel.value = String(codeList[0].value);
		} else {
			fieldModel.value = '';
		}
	}

	/**
	 * Show dialog box when creating a new batch
	 */
	private showNewBatchDialog = () => {
		const newBatchDialogModel = this.getClassicPanelDialogModel(
			FormType.ADD,
			'newBatch',
		);
		newBatchDialogModel.preSelectedParameters.push({
			name: BatchProcessFieldName.BSz_Batch_Code,
			value: this.createFormModel.fields.Batch_Code.value,
			paramType: CommonURLParam.BATCHES,
		});

		this.classicPanelDialogUtils.showCustomClassicPanelDialog(
			NewBatchDialog,
			newBatchDialogModel,
			this.recordSelectorTable,
		);
	};

	/**
	 * Show 'Process Batch' dialog from record slideout
	 */
	private showProcessBatchDialog = () => {
		const model: ProcessBatchDialogModel = {
			formModel: this.formModel,
		};
		this.dialogUtils
			.showCustomDialog(ProcessBatchDialog, model)
			.whenClosed((result) => {
				if (!result.wasCancelled) {
					this.recordSelectorTable.refreshAjaxTable(false);
					this.slideOut.reload();
					Notification.success(
						this.i18n.tr(
							'batch-processing-centre:processing-batch-with-ref',
							{
								ref: this.formModel.fields[
									BatchProcessFieldName.BSl_Batch_Reference
								].value,
							},
						),
					);
					const jobRef =
						this.formModel.fields[BatchProcessFieldName.BSl_Job_Ref]
							.value;
					const jobQueue =
						this.formModel.fields[
							BatchProcessFieldName.BSz_Job_Queue
						].value;
					if (jobRef && jobQueue) {
						const jobPriority =
							this.formModel.fields[
								BatchProcessFieldName.ZYc_Job_Priority
							].value;
						Notification.info(
							this.i18n.tr(
								jobPriority
									? 'batch-processing-centre:job-launched-to-queue-with-priority'
									: 'batch-processing-centre:job-launched-to-queue',
								{
									ref: jobRef,
									queue: jobQueue,
									priority: jobPriority,
								},
							),
						);
					}
				}
			});
	};

	/**
	 * Show 'Restore Batch' dialog from record slideout
	 */
	private showRestoreBatchDialog = () => {
		const currentRecordId = Number(
			this.formModel.fields[BatchProcessFieldName.BSi_Identity].value,
		);
		const restoreBatchDialogModel: ClassicPanelDialogModel = {
			functionName:
				this.functionName + '/' + currentRecordId + '/restore',
			recordId: currentRecordId,
			queryParams: this.queryParams,
			onFormLoad: this.formModelChanged,
			customisationConfig: this.customisationConfig,
			areaName: 'restoreBatch',
		};

		return this.classicPanelDialogUtils
			.showCustomClassicPanelDialogReturnOpen(
				RestoreBatchDialog,
				restoreBatchDialogModel,
			)
			.whenClosed((result) => {
				if (!result.wasCancelled) {
					this.recordSelectorTable.refreshAjaxTable(false);
					this.slideOut.reload();
				}
			});
	};

	/**
	 * Update 'myBatchesOnly' table filter
	 */
	private isShowMyBatchesOnlyChanged(value: boolean) {
		this.queryParams.set('myBatchesOnly', value.toString());
		this.refreshAjaxTableData();
	}

	/**
	 * Update 'showCancelled' table filter
	 */
	private isShowCancelledBatchesChanged(value: boolean) {
		this.queryParams.set('showCancelled', value.toString());
		this.refreshAjaxTableData();
	}

	/**
	 * Called by the view.
	 */
	private save() {
		return this.slideOut.save();
	}

	/**
	 * Called by the view.
	 */
	private edit() {
		return this.slideOut.edit();
	}

	/**
	 * Called by the view.
	 */
	private cancelEditOrClose() {
		return this.slideOut.cancelEditOrClose();
	}

	@computedFrom(
		'formModel',
		'formModel.fields.ZYz_IsProcessBatchAllowed.value',
	)
	public get isProcessBatchButtonVisible() {
		return (
			this.formModel &&
			this.formModel.fields.ZYz_IsProcessBatchAllowed.value === 'Y'
		);
	}

	@computedFrom(
		'formModel',
		'formModel.fields.ZYz_IsRestoreBatchAllowed.value',
	)
	public get isRestoreBatchButtonVisible() {
		return (
			this.formModel &&
			this.formModel.fields.ZYz_IsRestoreBatchAllowed.value === 'Y'
		);
	}

	@computedFrom('formModel', 'formModel.fields.BSn_US_ID_Locked.value')
	public get isBatchLocked() {
		return (
			this.formModel &&
			this.formModel.fields.BSn_US_ID_Locked.value !== '0'
		);
	}

	protected attached() {
		super.attached();
		this.subscribeToEvent(ClassicPanel.Events.PostPopulate, (data) => {
			if (
				this.recordSelectorTable &&
				data.source === BatchProcessingCentre.FUNCTION_NAME
			) {
				const fields = this.slideOut.formModel.fields;

				const totalValue: number = Number.parseInt(
					fields.ZYl_Batch_Progress_Total.value,
					10,
				);
				const currentValue: number = Number.parseInt(
					fields.ZYl_Batch_Progress_Current.value,
					10,
				);
				const batchProgressStatus: BatchProgressStatus = fields
					.ZYz_Batch_Progress_Status.value as BatchProgressStatus;

				this.slideoutProgressBar = this.getProgressBar(
					totalValue,
					currentValue,
					batchProgressStatus,
					new ProgressBar(undefined),
				);
				this.slideoutProgressBar.label = this.computeProgressBarLabel(
					totalValue,
					currentValue,
					batchProgressStatus,
				);
			}
		});
		this.bindCustomisationEditorShortcutKey(
			this.customisationEditorSelectDialogManager,
		);
	}
}
